import React, { FC, ChangeEvent, Fragment, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { get, debounce } from 'lodash';
import { useFormikContext } from 'formik';
import LibraryAddIcon from '@material-ui/icons/LibraryAdd';

import { ClientFollowMappingFragment, ClientFollowMappingStatus } from 'api';
import { Panel, ValidatedSwitch, ValidatedTextField } from 'components';

import { ALL_MAPPING_KEYS, FOLLOW_KEYS, ASSET_KEYS } from '../constants';
import GroupsEdit from '../GroupsEdit';
import UsersEdit from '../UsersEdit';
import DepartmentsEdit from '../DepartmentsEdit';
import LocationsEdit from '../LocationsEdit';
import ConjunctionEdit from './ConjunctionEdit';

import Names from './Names';

interface IProps {
  mapping: ClientFollowMappingFragment;
}

const useStyles = makeStyles(theme => ({
  dividerHeader: {
    margin: '12px -16px 24px -16px',
  },
  divider: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  padding: {
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
  },
  blank: {
    color: 'transparent',
  },
}));

const Mapping: FC<IProps> = ({ mapping }) => {
  const { t } = useTranslation();

  const classes = useStyles();
  const formik = useFormikContext();
  const isPopulated = hasKeys();
  const debouncedSave = useCallback(debounce(formik.submitForm, 1000), []);

  function checkedFn(value: ClientFollowMappingStatus) {
    return value === ClientFollowMappingStatus.Active;
  }

  function hasKeys() {
    return !!ALL_MAPPING_KEYS.map(key => get(mapping, key, []).length).reduce(
      (prev, next) => prev + next,
      0
    );
  }

  function hasAssetKeys() {
    return !!ASSET_KEYS.map(key => get(mapping, key, []).length).reduce(
      (prev, next) => prev + next,
      0
    );
  }

  function hasFollowKeys() {
    return !!FOLLOW_KEYS.map(key => get(mapping, key, []).length).reduce(
      (prev, next) => prev + next,
      0
    );
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>, value: boolean) {
    const status = value ? ClientFollowMappingStatus.Active : ClientFollowMappingStatus.Disabled;
    formik.setFieldValue('mapping_status', status);

    setTimeout(() => {
      void formik.submitForm();
    });
  }

  function renderToggle() {
    if (!hasFollowKeys() || !hasAssetKeys()) {
      return null;
    }

    return (
      <ValidatedSwitch
        name="mapping_status"
        color="primary"
        label={t('common:status')}
        onChange={handleChange}
        checkedFn={checkedFn}
      />
    );
  }

  function renderEditBody() {
    return (
      <Fragment>
        <Divider className={classes.dividerHeader} />
        <DepartmentsEdit mapping={mapping} />
        <ConjunctionEdit mapping={mapping} />
        <LocationsEdit mapping={mapping} />
        {hasFollowKeys() && (
          <Fragment>
            <Divider className={classes.divider} />
            <GroupsEdit mapping={mapping} />
            <Divider className={classes.divider} />
            <UsersEdit mapping={mapping} />
          </Fragment>
        )}
        <Divider className={classes.divider} />
      </Fragment>
    );
  }

  function renderViewBody() {
    if (!isPopulated) {
      return null;
    }

    return (
      <Fragment>
        <Divider className={classes.dividerHeader} />
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <LibraryAddIcon fontSize="large" className={classes.blank} />
          </Grid>
          <Grid item xs>
            <Grid container spacing={3}>
              <Names
                title={t('common:department_plural')}
                listKey="departments"
                mapping={mapping}
              />
              <Names title={t('common:location_plural')} listKey="locations" mapping={mapping} />
              <Names
                title={t('admin:requiredGroups')}
                listKey="required_groups"
                mapping={mapping}
              />
              <Names
                title={t('admin:suggestedGroups')}
                listKey="suggested_groups"
                mapping={mapping}
              />
              <Names title={t('admin:requiredUsers')} listKey="required_users" mapping={mapping} />
              <Names
                title={t('admin:suggestedUsers')}
                listKey="suggested_users"
                mapping={mapping}
              />
            </Grid>
          </Grid>
          <Grid item>
            <LibraryAddIcon fontSize="large" className={classes.blank} />
          </Grid>
        </Grid>
      </Fragment>
    );
  }

  function renderHeader(editing: boolean) {
    if (editing) {
      return (
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <LibraryAddIcon color={isPopulated ? 'primary' : 'disabled'} fontSize="large" />
          </Grid>
          <Grid item xs>
            <ValidatedTextField
              name="title"
              i18nKey="admin:onboardingAdd"
              color="primary"
              TextFieldProps={{ margin: 'none', label: null }}
              onChange={(event: ChangeEvent<HTMLInputElement>, onChange) => {
                onChange(event);
                void debouncedSave();
              }}
            />
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <LibraryAddIcon fontSize="large" color={isPopulated ? 'primary' : 'disabled'} />
        </Grid>
        <Grid item xs>
          <Typography variant="subtitle2">{mapping.title}</Typography>
        </Grid>
      </Grid>
    );
  }

  function renderBody(editing: boolean) {
    return editing ? renderEditBody() : renderViewBody();
  }

  return <Panel header={renderHeader} body={renderBody} toggle={renderToggle} />;
};

export default Mapping;
